// Styles for the supplier "Edit Order" editor (COMY1-292). Matches the existing
// ViewJobModal / ManagePricelists visual system (greens, greys, 8px radius).

export const editOrderContainerStyle = {
  flex: 1,
  minWidth: 0,
  background: { xs: "#fff", md: "#f8f7fa" },
  padding: { xs: "16px", md: "32px" },
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  overflowY: "auto",
};

export const editSubtitleStyle = {
  fontSize: "14px",
  fontWeight: 400,
  color: "rgba(62, 63, 76, 0.6)",
};

// Read-only visit-fee row shown at the top of the editor for a visit-originated
// order (COMY1-292) — grey/disabled look, always part of the total.
export const visitRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  border: "1px solid rgba(47, 43, 61, 0.22)",
  backgroundColor: "rgba(47, 43, 61, 0.06)",
  borderRadius: "8px",
  padding: "14px 16px",
};

export const lineCardStyle = {
  position: "relative",
  backgroundColor: "#fff",
  border: "1px solid rgba(234, 234, 234, 1)",
  borderRadius: "8px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

export const cardTrashButtonStyle = {
  position: "absolute",
  top: "8px",
  insetInlineEnd: "8px",
  color: "rgba(47, 43, 61, 0.7)",
  // Dimmed + non-interactive when it's the only line (can't empty the order).
  "&.Mui-disabled": { opacity: 0.35 },
};

export const fieldLabelStyle = {
  fontSize: "14px",
  fontWeight: 400,
  color: "rgba(47, 43, 61, 0.9)",
  marginBottom: "4px",
};

// Header row: "Choose Service or Product" + "Choose A Service"/"Choose Product"
// side by side (Figma: two ~298px columns, 24px gap), wrapping on narrow panels.
export const headerRowStyle = {
  display: "flex",
  gap: "24px",
  flexWrap: "wrap",
  alignItems: "flex-start",
};

export const headerColStyle = {
  flex: "1 1 240px",
  minWidth: "200px",
  maxWidth: "320px",
};

// Make Select menus drop BELOW the input (MUI's default overlays the menu on
// top of the field) with a capped, scrollable height for long service lists.
// Direction-aware: in RTL (Hebrew) the menu hangs from the input's right edge
// and grows leftward; in LTR from the left edge. (MUI does not auto-flip
// anchorOrigin for theme direction.)
export const getSelectMenuProps = (isRtl) => ({
  anchorOrigin: { vertical: "bottom", horizontal: isRtl ? "right" : "left" },
  transformOrigin: { vertical: "top", horizontal: isRtl ? "right" : "left" },
  PaperProps: { sx: { maxHeight: 320, mt: "4px" } },
});

export const selectStyle = {
  backgroundColor: "#fff",
  borderRadius: "6px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(47, 43, 61, 0.22)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(36, 139, 112, 1)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(36, 139, 112, 1)",
  },
};

export const textFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "rgba(47, 43, 61, 0.22)" },
    "&:hover fieldset": { borderColor: "rgba(36, 139, 112, 1)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(36, 139, 112, 1)" },
  },
};

// Read-only field look for prefilled values (an existing product's name/price)
// — grey fill + muted text, matching Figma's disabled inputs.
export const readOnlyFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    backgroundColor: "rgba(47, 43, 61, 0.06)",
    "& fieldset": { borderColor: "rgba(47, 43, 61, 0.22)" },
    "&:hover fieldset": { borderColor: "rgba(47, 43, 61, 0.22)" },
    // Read-only: no blue focus ring — keep the grey border on focus too.
    "&.Mui-focused fieldset": {
      borderColor: "rgba(47, 43, 61, 0.22)",
      borderWidth: "1px",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "rgba(47, 43, 61, 0.6)",
    WebkitTextFillColor: "rgba(47, 43, 61, 0.6)",
    cursor: "default",
  },
};

// Compact single-line variant row (Figma: [remove] variant · amount · unit …
// price). The left group is packed at the start; the price is pushed to the far
// end via space-between (reliable in both RTL and LTR).
export const variantRowStyle = {
  display: "flex",
  // Desktop: one line (price pushed to the far end). Mobile: the fields are
  // wider than the panel, so let the row wrap onto multiple lines instead of
  // bleeding off the edge (COMY1-292).
  flexWrap: { xs: "wrap", sm: "nowrap" },
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};

export const variantLeftGroupStyle = {
  // Desktop: grow to fill the row so the price is pushed to the far end (the
  // group's own items stay packed at its start). Mobile: take the full row and
  // wrap its own children (variant on top, amount + unit below).
  flex: { xs: "1 1 100%", sm: "1 1 auto" },
  minWidth: 0,
  display: "flex",
  flexWrap: { xs: "wrap", sm: "nowrap" },
  alignItems: "center",
  gap: "12px",
};

// Grey, non-editable unit chip shown for LISTED variants (the unit is fixed by
// the variant's pricing). Not-listed variants use an editable unit dropdown.
export const unitChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  height: "38px",
  padding: "0 14px",
  borderRadius: "6px",
  border: "1px solid rgba(47, 43, 61, 0.22)",
  backgroundColor: "rgba(47, 43, 61, 0.06)",
  color: "rgba(47, 43, 61, 0.6)",
  fontSize: "14px",
  whiteSpace: "nowrap",
};

// Inline price shown at the end of a variant's dropdown value ("· $5/item").
// Same dark text as the variant name (Figma text-primary).
export const variantInlinePriceStyle = {
  color: "rgba(47, 43, 61, 0.9)",
  fontSize: "14px",
  whiteSpace: "nowrap",
};

// "Amount:" prefix inside the amount field (Figma "Amount: 3") — same dark text
// as the value.
export const amountAdornmentStyle = {
  color: "rgba(47, 43, 61, 0.9)",
  fontSize: "14px",
  whiteSpace: "nowrap",
  marginInlineEnd: "4px",
};

export const variantTrashStyle = {
  color: "rgba(47, 43, 61, 0.7)",
  padding: "4px",
  flex: "0 0 auto",
};

export const variantPriceBoxStyle = {
  display: "flex",
  alignItems: "center",
  fontSize: "14px",
  fontWeight: 600,
  color: "rgba(47, 43, 61, 0.9)",
  whiteSpace: "nowrap",
};

export const addLinkStyle = {
  // Dark neutral + circled-plus icon per Figma (not the green link style).
  color: "rgba(47, 43, 61, 0.9)",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  textTransform: "none",
  padding: 0,
  justifyContent: "flex-start",
  // Only as wide as its content, so the whole line isn't a click target
  // (in a flex column, buttons would otherwise stretch full width).
  alignSelf: "flex-start",
  minWidth: 0,
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
};

export const editOrderLinkStyle = {
  color: "rgba(36, 139, 112, 1)",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  textTransform: "none",
  padding: 0,
  minWidth: "auto",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
};

export const fieldErrorStyle = {
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
};

// Variant-row errors float BELOW the input (absolute) so showing them after a
// failed save doesn't shift the row layout. Container needs position:relative.
// Compact (12px line) so it fits inside the 16px row gap without touching the
// next row — no reserved space, no jump.
export const floatErrorStyle = {
  position: "absolute",
  top: "100%",
  insetInlineStart: 0,
  marginTop: "1px",
  whiteSpace: "nowrap",
  color: "#d32f2f",
  fontSize: "12px",
  lineHeight: "12px",
};
